import React, { ReactElement, useState } from 'react';
import cx from 'classnames';
import { PageButton, PageButtonType, Props as PageButtonProps } from './PageButton';

type Props = {
  activePage: number;
  boundaryRange?: number;
  children?: never;
  className?: string;
  collapse?: boolean;
  minPagesToShow?: number;
  modal?: ReactElement;
  onPageChange?: (page: string | number) => void;
  pageButtonFn?: (props: PageButtonProps) => ReactElement;
  pageUrlFn?: (page: number | string) => string;
  pageUrlPrefix: string;
  pageUrlSuffix?: string;
  showPrevNext?: boolean;
  siblingRange?: number;
  totalPages: number;
};

type PageObj = {
  isActive?: boolean;
  page?: number;
  type?: PageButtonType;
};

export const Pagination = (props: Props) => {
  const {
    activePage,
    boundaryRange = 2,
    className,
    collapse = true,
    minPagesToShow = 2,
    modal,
    onPageChange,
    pageButtonFn = (props) => <PageButton {...props} />,
    pageUrlFn,
    pageUrlPrefix,
    pageUrlSuffix,
    showPrevNext,
    siblingRange = 1,
    totalPages,
  } = props;

  const [modalActive, setModalActive] = useState(false);

  if (totalPages < minPagesToShow) return null;

  const paginationModal =
    modal &&
    React.cloneElement(modal, {
      active: modalActive,
      closeModal: () => setModalActive(false),
    });

  let pagesObjects: PageObj[] = [];

  const createPageButtonObject = (i?: number): PageObj => {
    if (!i) {
      return {
        type: PageButtonType.ELLIPSIS,
      };
    }

    return {
      isActive: activePage === i,
      page: i,
    };
  };

  const createEllipsisBetween = (aPart: PageObj[], bPart: PageObj[]) => {
    if (aPart.length === 0 || bPart.length === 0) {
      return [];
    }

    const lastPartA = aPart[aPart.length - 1].page ?? 0;
    const firstPartB = bPart[0].page;

    if (lastPartA + 1 !== firstPartB) {
      return [createPageButtonObject()];
    }

    return [];
  };

  const firstPage = 1;

  if (collapse && totalPages > 3) {
    /**
       Props:
       - Active page: 10
       - Total pages: 50
       - Boundary pages range: 2 | ex. 1,2 & 49,59
       - Siblings pages range: 1 | ex. 9,11

       Pagination Demo:
       << < 1 2 ... 9 [10] 11 ... 49 50 > >>
       ^     ^      ^       ^
       |     |      |       |
       startMax siblingMin/Max endMin
       */
    const siblingMin = activePage - siblingRange > firstPage ? activePage - siblingRange : firstPage; // ex. 9
    const siblingMax = activePage + siblingRange < totalPages ? activePage + siblingRange : totalPages; // ex. 11
    const startMax = boundaryRange <= siblingMin ? boundaryRange : siblingMin; // ex. 2
    const endMin = totalPages - boundaryRange + 1 >= siblingMax ? totalPages - boundaryRange + 1 : siblingMax; // ex. 49

    const [firstPart, secondPart, thirdPart]: [PageObj[], PageObj[], PageObj[]] = [[], [], []];

    // generate 1/3 part of the pagination
    for (let i = firstPage; i <= startMax; i += 1) {
      if (i < siblingMin) {
        firstPart.push(createPageButtonObject(i));
      }
    }

    // generate 2/3 part of the pagination
    for (let i = siblingMin; i <= siblingMax; i += 1) {
      secondPart.push(createPageButtonObject(i));
    }

    // generate 3/3 part of the pagination
    for (let i = endMin; i <= totalPages; i += 1) {
      // if (!(endMin === totalPages && endMin === i) && !(endMin === activePage && activePage === i) && !(endMin === totalPages && endMin === i)) {
      if (i > siblingMax) {
        thirdPart.push(createPageButtonObject(i));
      } // prevents overlap
    }

    pagesObjects = [
      ...firstPart,
      ...createEllipsisBetween(firstPart, secondPart),
      ...secondPart,
      ...createEllipsisBetween(secondPart, thirdPart),
      ...thirdPart,
    ];
  } else {
    for (let i = firstPage; i <= totalPages; i += 1) {
      pagesObjects.push(createPageButtonObject(i));
    }
  }

  return (
    <>
      <div className={cx('man-ui text-center', className)}>
        <nav className="pagination">
          {showPrevNext &&
            totalPages > 1 &&
            pageButtonFn({
              isActive: false,
              key: 0,
              onPageChange,
              page: activePage > 1 ? activePage - 1 : activePage,
              pageUrlFn,
              pageUrlPrefix,
              pageUrlSuffix,
              type: PageButtonType.PREV,
            })}

          {pagesObjects.map(({ isActive, page, type }, index) =>
            pageButtonFn({
              isActive,
              key: index,
              onEllipsisClick: () => setModalActive(true),
              onPageChange,
              page,
              pageUrlFn,
              pageUrlPrefix,
              pageUrlSuffix,
              type,
            }),
          )}

          {showPrevNext &&
            totalPages > 1 &&
            pageButtonFn({
              isActive: false,
              key: totalPages + 1,
              onPageChange,
              page: activePage === totalPages ? activePage : activePage + 1,
              pageUrlFn,
              pageUrlPrefix,
              pageUrlSuffix,
              type: PageButtonType.NEXT,
            })}
        </nav>
      </div>
      {paginationModal}
    </>
  );
};
